import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ENGINE,
  SYNC_FILE_TYPES,
  defaultCheckedKeys,
  keysForPhase,
  todayIso,
} from '../config/eodPipeline';
import { xhrStream } from '../utils/xhrStream';
import { useEodSyncControl } from '../hooks/useEodSyncControl';
import EodConsole from '../components/eod/EodConsole';
import FileTypeGrid from '../components/eod/FileTypeGrid';
import PhaseScheduleBanner from '../components/eod/PhaseScheduleBanner';
import '../styles/eod-pipeline.css';

export default function EodSyncPage() {
  const { send: syncControl } = useEodSyncControl();
  const [fromDate, setFromDate] = useState(todayIso);
  const [toDate, setToDate] = useState(todayIso);
  const [selected, setSelected] = useState(() => new Set(defaultCheckedKeys(SYNC_FILE_TYPES)));
  const [log, setLog] = useState('Ready...');
  const [barFile, setBarFile] = useState(0);
  const [barFii, setBarFii] = useState(0);
  const [showBarFile, setShowBarFile] = useState(true);
  const [showBarFii, setShowBarFii] = useState(false);
  const [running, setRunning] = useState(false);
  const [controlState, setControlState] = useState('idle');

  useEffect(() => {
    document.body.classList.add('eod-pipeline-body');
    return () => document.body.classList.remove('eod-pipeline-body');
  }, []);

  const fileKeys = useMemo(() => {
    const keys = [];
    SYNC_FILE_TYPES.forEach((sec) => {
      sec.items.forEach((it) => {
        if (selected.has(it.value) && it.group === 'file') keys.push(it.value);
      });
    });
    return keys;
  }, [selected]);

  const hasFii = selected.has('fii_dii_cash');

  const validateDates = useCallback(() => {
    if (!fromDate || !toDate) {
      alert('Please select From Date and To Date.');
      return false;
    }
    if (fromDate > toDate) {
      alert('From Date cannot be after To Date.');
      return false;
    }
    return true;
  }, [fromDate, toDate]);

  const toggleKey = useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectPhase = useCallback((phaseNum, checked) => {
    const keys = keysForPhase(SYNC_FILE_TYPES, phaseNum);
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => { if (checked) next.add(k); else next.delete(k); });
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const all = new Set();
    SYNC_FILE_TYPES.forEach((s) => s.items.forEach((i) => all.add(i.value)));
    setSelected(all);
  }, []);

  const selectNone = useCallback(() => setSelected(new Set()), []);

  const setControlUi = useCallback((state) => {
    setControlState(state);
  }, []);

  const handleControl = useCallback(async (action) => {
    const j = await syncControl(action);
    if (!j.ok) return j;
    if (action === 'pause') {
      setControlUi('paused');
      setLog((l) => `${l}\n⏸ Pause requested…\n`);
    } else if (action === 'resume') {
      setControlUi('running');
      setLog((l) => `${l}\n▶ Resume requested…\n`);
    } else if (action === 'stop') {
      setControlUi('running');
      setLog((l) => `${l}\n⏹ Stop requested… finishing current file then aborting.\n`);
    }
    return j;
  }, [syncControl, setControlUi]);

  const runDownload = useCallback(async () => {
    if (!validateDates()) return;
    if (running) {
      alert('A download is already running.');
      return;
    }
    if (fileKeys.length === 0 && !hasFii) {
      alert('Please select at least one file type to download.');
      return;
    }

    setRunning(true);
    await syncControl('running');
    setControlUi('running');
    setShowBarFile(fileKeys.length > 0);
    setShowBarFii(hasFii);
    setBarFile(0);
    setBarFii(0);
    setLog('');

    let logAcc = '';

    try {
      if (fileKeys.length > 0) {
        setLog('⏳ Starting file downloads by selected phases (skips Sat/Sun + NSE holidays)…\n');
        const fd = new FormData();
        fd.append('from_date', fromDate);
        fd.append('to_date', toDate);
        fd.append('selected_keys', JSON.stringify(fileKeys));

        const result = await xhrStream(`${ENGINE}process_download.php`, fd, {
          onLog: setLog,
          onProgress: setBarFile,
          barFn: 'setBar',
        });
        logAcc = `${result}\n\n`;
      }

      const ctrl = await syncControl('status');
      if (hasFii && (!ctrl.status || ctrl.status !== 'stop')) {
        const fd2 = new FormData();
        fd2.append('from_date', fromDate);
        fd2.append('to_date', toDate);
        await xhrStream(`${ENGINE}process_fii_dii_sync.php`, fd2, {
          onLog: (text) => setLog(logAcc + text),
          onProgress: setBarFii,
          barFn: 'setBarFII',
          logPrefix: logAcc,
        });
      } else if (hasFii && ctrl.status === 'stop') {
        setLog((l) => `${l}\n⏹ Stopped — FII/DII sync skipped.\n`);
      }

      setLog((l) => `${l}\n\n══════════════════════════════════════\n✅ ALL DOWNLOADS COMPLETE\n══════════════════════════════════════`);
    } catch (e) {
      setLog((l) => `${l}\n❌ Error: ${e.message}`);
    }

    setRunning(false);
    await syncControl('running');
    setControlUi('idle');
  }, [validateDates, running, fileKeys, hasFii, fromDate, toDate, syncControl, setControlUi]);

  const clearLog = useCallback(() => {
    setLog('Ready...');
    setBarFile(0);
    setBarFii(0);
  }, []);

  const showControls = controlState === 'running' || controlState === 'paused';

  return (
    <div className="eod-pipeline-page">
      <div className="eod-sync-split">
        <div className="eod-sync-controls">
          <div className="eod-sync-controls-scroll">
            <div className="sync-controls-head">
              <h3><i className="bi bi-cloud-download" /> EOD Market Sync</h3>
              <div className="sync-info">
                <div><b>Mode:</b> Select file types (by Phase) → Set date range → Download</div>
                <div><b>Auto-skip:</b> Already downloaded files are automatically skipped</div>
              </div>
            </div>

            <PhaseScheduleBanner mode="sync" />

            <div className="date-action-row">
              <div className="date-group">
                <label htmlFor="sync-from">From Date</label>
                <input id="sync-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} required />
              </div>
              <div className="date-group">
                <label htmlFor="sync-to">To Date</label>
                <input id="sync-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} required />
              </div>
              <div className="action-buttons">
                <button type="button" className="btnPrimary" onClick={runDownload} disabled={running}>
                  {running ? <><span className="eod-spin">⏳</span> Downloading...</> : <><i className="bi bi-cloud-download" /> Download Selected Files</>}
                </button>
                {showControls && (
                  <>
                    <button
                      type="button"
                      className="btnPrimary"
                      style={{ background: 'var(--amber, #f59e0b)' }}
                      disabled={controlState === 'paused'}
                      onClick={() => handleControl('pause')}
                    >
                      <i className="bi bi-pause-fill" /> Pause
                    </button>
                    <button
                      type="button"
                      className="btnPrimary"
                      style={{ background: 'var(--green, #10b981)' }}
                      disabled={controlState === 'running'}
                      onClick={() => handleControl('resume')}
                    >
                      <i className="bi bi-play-fill" /> Resume
                    </button>
                    <button
                      type="button"
                      className="btnPrimary"
                      style={{ background: 'var(--red, #ef4444)' }}
                      onClick={() => handleControl('stop')}
                    >
                      <i className="bi bi-stop-fill" /> Stop
                    </button>
                  </>
                )}
              </div>
            </div>

            <FileTypeGrid
              sections={SYNC_FILE_TYPES}
              selected={selected}
              onToggle={toggleKey}
              onSelectPhase={selectPhase}
              onSelectAll={selectAll}
              onSelectNone={selectNone}
              metaClass="ft-folder"
            />

            <div className="control-hint">
              <b>Phased by Recommended Schedule:</b> Phase 1 (Equities &amp; Spot @ ~16:30 IST) • Phase 2 (Derived/Web @ ~16:45 IST) • Phase 3 (Participant &amp; FII @ 18:30 IST + retries to 20:00).
              <b> Pause / Stop:</b> Use Pause to hold between days, Resume to continue, Stop to abort.
              <b> Cron automation:</b> Each phase download chains to import — Phase 3 participant data appears on <a href="/participant">Participant OI</a>.
            </div>

            <div className="progress-section">
              {showBarFile && (
                <div className="progress-row">
                  <span className="progress-label">File Download</span>
                  <div className="progress-wrap">
                    <div className="progress-bar-fill bar-download" style={{ width: `${barFile}%` }}>{barFile}%</div>
                  </div>
                </div>
              )}
              {showBarFii && (
                <div className="progress-row">
                  <span className="progress-label">FII/DII Cash</span>
                  <div className="progress-wrap">
                    <div className="progress-bar-fill bar-fii" style={{ width: `${barFii}%` }}>{barFii}%</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <EodConsole
          log={log}
          onClear={clearLog}
          subtitle="Streaming server output"
        />
      </div>
    </div>
  );
}